import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const KrushiKarjMitraShifaraspatsaForm = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'Krushi_Karj_Mitra_Shifaraspatsa.pdf' : 'Krushi_Karj_Mitra_Recommendation_Letter.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          .page {
            width: 210mm;
            height: 297mm;
            padding: 20mm 20mm;
            margin: 0 auto;
            box-sizing: border-box;
            background-color: white;
            box-shadow: 0 0 15px rgba(0,0,0,0.1);
            position: relative;
          }
          
          .dashed-line {
            border-bottom: 1px dashed black;
            display: inline-block;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page {
              margin: 0 !important;
              box-shadow: none !important;
              width: 210mm !important;
              height: 297mm !important;
              border: none !important;
            }
            .no-print {
              display: none !important;
            }
            .min-h-screen {
              min-height: auto !important;
              padding: 0 !important;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4 no-print">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div ref={componentRef} className="page text-black text-[16px] leading-[1.8] relative flex flex-col pt-8 px-6">
         
         {/* Top Header */}
         <div className="w-full mb-6">
            <div className="font-bold text-[14px] border-b-[3px] border-black pb-1">
               {language === 'marathi' ? 'शासन परिपत्रक क्रमांक: योजना-२०२०/प्र.क्र. ३४/वित्त-३' : 'Government Circular No.: Yojana-2020/C.R. 34/Finance-3'}
            </div>
            <div className="border-b border-black mt-[2px]"></div>
         </div>

         {/* Part 1 */}
         <div className="flex flex-col w-full mb-8">
            <div className="font-bold text-[19px] text-center mb-6">
               {language === 'marathi' ? 'कृषी कर्ज मित्रांस द्यावयाच्या सेवाशुल्कासाठी शेतकरी शिफारस पत्र' : 'Farmer Recommendation Letter for Service Charges to Krushi Karj Mitra'}
            </div>
            
            <div className="text-justify indent-12 mb-6 leading-[2.0]">
               {language === 'marathi' ? (
                 <>
                   मी श्री./श्रीमती <span className="dashed-line w-[300px] mx-2"></span> राहणार <span className="dashed-line w-[150px] mx-2"></span>
                   ता. <span className="dashed-line w-[100px] mx-2"></span> जि.<span className="dashed-line w-[80px] mx-2"></span> येथील रहिवाशी असून मी <span className="dashed-line w-[100px] mx-2"></span> बँकेच्या <span className="dashed-line w-[150px] mx-2"></span>
                   - शाखेतून सन २०२१-२२ खरीप/रब्बी हंगामात नवीन पीक कर्ज / मध्यम मुदत कर्ज / दीर्घ मुदत कर्ज
                   घेतले त्यासाठी मला श्री./श्रीमती<span className="dashed-line w-[200px] mx-2"></span> या कृषी कर्ज मित्राने मंजुरीसाठी
                   सहाय्य केले. त्यास नियमाप्रमाणे सेवाशुल्क देण्यास माझी हरकत नसून या बाबत माझ्यावर कोणताही
                   दबाव आणण्यात आलेला नाही. याबाबत माझी कोणतीही तक्रार नाही.
                 </>
               ) : (
                 <>
                   I Mr./Mrs. <span className="dashed-line w-[300px] mx-2"></span> Resident of <span className="dashed-line w-[150px] mx-2"></span>
                   Tal. <span className="dashed-line w-[100px] mx-2"></span> Dist.<span className="dashed-line w-[80px] mx-2"></span> am a resident here and I have taken a new crop loan/medium-term loan/long-term loan from <span className="dashed-line w-[100px] mx-2"></span> bank's <span className="dashed-line w-[150px] mx-2"></span>
                   - branch in the year 2021-22 Kharif/Rabi season, for which Mr./Mrs.<span className="dashed-line w-[200px] mx-2"></span> Krushi Karj Mitra, helped me for approval. 
                   I have no objection to paying him/her service charges as per rules and no pressure has been brought upon me in this regard. I have no complaint in this regard.
                 </>
               )}
            </div>

            <div className="flex justify-end pr-16 mt-4">
               <div className="flex flex-col items-center font-bold text-[16px] space-y-2">
                  <div className="mb-4">{language === 'marathi' ? 'स्वाक्षरी' : 'Signature'}</div>
                  <div className="w-full text-left">{language === 'marathi' ? 'शेतकऱ्याचे नांव:' : "Farmer's Name:"}</div>
                  <div className="w-full text-left">{language === 'marathi' ? 'मो.नं.:' : 'Mob No.:'}</div>
               </div>
            </div>
         </div>

         {/* Spacer between parts */}
         <div className="h-6"></div>

         {/* Part 2 */}
         <div className="flex flex-col w-full">
            <div className="font-bold text-[19px] text-center mb-6 flex flex-col">
               <span>{language === 'marathi' ? 'कृषी कर्ज मित्रांस द्यावयाच्या सेवाशुल्कासाठी बँक अधिकारी / विकास सोसायटीचे' : 'Recommendation letter from Bank Officer / Secretary of'}</span>
               <span className="mt-1">{language === 'marathi' ? 'सचिव यांचे शिफारस पत्र' : 'Development Society for service charges to Krushi Karj Mitra'}</span>
            </div>
            
            <div className="text-justify indent-12 mb-6 leading-[2.0]">
               {language === 'marathi' ? (
                 <>
                   सन २०२१-२२ या आर्थिक वर्षात खरीप /रब्बी हंगामात श्री./श्रीमती <span className="dashed-line w-[180px] mx-2"></span> राहणार -
                   <span className="dashed-line w-[100px] mx-1"></span>ता.<span className="dashed-line w-[80px] mx-1"></span> जि.<span className="dashed-line w-[80px] mx-1"></span> यांना नवीन पीक कर्ज / मध्यम मुदत कर्ज / दीर्घ मुदत कर्ज मंजूर
                   केले आहे. कर्जदाराने अर्ज केल्याप्रमाणे श्री./श्रीमती <span className="dashed-line w-[150px] mx-2"></span> या कृषी कर्ज मित्रास नियमाप्रमाणे
                   सेवाशुल्क देण्यास शिफारस करण्यात येत आहे.
                 </>
               ) : (
                 <>
                   In the financial year 2021-22 during Kharif/Rabi season, Mr./Mrs. <span className="dashed-line w-[180px] mx-2"></span> Resident of 
                   <span className="dashed-line w-[100px] mx-1"></span>Tal.<span className="dashed-line w-[80px] mx-1"></span> Dist.<span className="dashed-line w-[80px] mx-1"></span> has been sanctioned a new crop loan/medium-term loan/long-term loan. 
                   As per the application of the borrower, Mr./Mrs. <span className="dashed-line w-[150px] mx-2"></span> Krushi Karj Mitra, is being recommended to be paid service charges as per rules.
                 </>
               )}
            </div>

            <div className="flex justify-end pr-16 mt-4">
               <div className="flex flex-col items-center font-bold text-[16px] space-y-2">
                  <div className="mb-4">{language === 'marathi' ? 'स्वाक्षरी' : 'Signature'}</div>
                  <div className="w-full text-center">{language === 'marathi' ? 'बँक अधिकाऱ्याचे नांव' : 'Name of Bank Officer'}</div>
                  <div className="w-full text-center">{language === 'marathi' ? 'हुद्दा व शिक्का' : 'Designation and Seal'}</div>
               </div>
            </div>
         </div>

      </div>
    </div>
  );
};

export default KrushiKarjMitraShifaraspatsaForm;
